import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { fadeUp, waLink } from "@/lib/site-utils";
import { IMAGES, CONSTRUCTION_SERVICES, TRAVEL_SERVICES } from "@/data/site";

const Chapter = ({ id, no, accent, kicker, title, titleEn, desc, services, img, alt, msg, reverse, testid }) => {
  const accentText = accent === "jcb" ? "text-jcb" : "text-terra";
  const accentBg = accent === "jcb" ? "bg-jcb" : "bg-terra";
  return (
    <section id={id} data-testid={testid} className="relative border-t border-white/10 py-24 md:py-36">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className={`grid lg:grid-cols-2 gap-14 lg:gap-24 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}>
          {/* Text */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            className="lg:[direction:ltr]"
          >
            <div className="flex items-baseline gap-5 mb-6">
              <span className={`font-khand font-700 text-7xl md:text-8xl ${accentText} leading-none`}>{no}</span>
              <span className="font-plex text-xs tracking-[0.25em] uppercase text-neutral-400">{kicker}</span>
            </div>
            <h2 className="font-khand font-700 uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl text-[#f5f5f0]">
              {title}
              <span className={`block ${accentText}`}>{titleEn}</span>
            </h2>
            <p className="mt-6 max-w-lg font-hind text-neutral-300 leading-relaxed">{desc}</p>

            <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 max-w-lg">
              {services.map((s, i) => (
                <li key={i} className="flex items-center gap-3 font-hind text-neutral-200">
                  <Check size={16} className={accentText} strokeWidth={3} />
                  <span>
                    {s.hi} <span className="text-neutral-500 text-sm">/ {s.en}</span>
                  </span>
                </li>
              ))}
            </ul>

            <a
              data-testid={`${testid}-book-btn`}
              href={waLink(msg)}
              target="_blank"
              rel="noreferrer"
              className={`group mt-10 inline-flex items-center gap-3 ${accentBg} text-black px-7 py-3.5 font-khand font-600 text-lg tracking-wide hover:bg-white transition-colors duration-200`}
            >
              अभी बुक करें / Book Now
              <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
          </motion.div>

          {/* Image */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            className="relative lg:[direction:ltr]"
          >
            <div className="relative overflow-hidden border border-white/10 aspect-[4/5]">
              <img src={img} alt={alt} className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" />
              <div className={`absolute bottom-0 left-0 h-1.5 w-1/3 ${accentBg}`} />
            </div>
            <div className={`absolute -top-4 ${reverse ? "-left-4" : "-right-4"} hidden md:block border border-white/15 bg-stone px-5 py-3`}>
              <span className="font-plex text-[10px] tracking-[0.2em] text-neutral-400 uppercase block">Trusted in</span>
              <span className="font-khand text-xl text-[#f5f5f0]">Ramganjmandi · Kota</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const Businesses = () => (
  <>
    <Chapter
      id="construction"
      testid="construction-section"
      no="01"
      accent="jcb"
      kicker="निर्माण / CONSTRUCTION"
      title="JCB मशीन"
      titleEn="EARTHWORK & MORE"
      desc="2 JCB मशीनें और अनुभवी ऑपरेटर के साथ खुदाई, लेवलिंग, सड़क कार्य और प्लॉट सफाई। तुरंत बुकिंग के लिए कॉल या WhatsApp करें — समय पर, भरोसेमंद और दमदार काम।"
      services={CONSTRUCTION_SERVICES}
      img={IMAGES.jcbHero}
      alt="SS Construction JCB backhoe loader Ramganjmandi"
      msg="नमस्ते SS Construction! मुझे JCB बुकिंग चाहिए। कार्य: "
    />
    <Chapter
      id="travels"
      testid="travels-section"
      no="02"
      accent="terra"
      kicker="यात्रा / TRAVELS"
      title="टैक्सी सेवा"
      titleEn="RIDE ANYWHERE"
      desc="Maruti Ertiga (Taxi Permit) परिवार और आउटस्टेशन यात्रा के लिए, और Tata Tigor लोकल व आउटस्टेशन बुकिंग के लिए। एयरपोर्ट, रेलवे स्टेशन, शादी और टूर — साफ-सुथरी गाड़ी, सुरक्षित सफर।"
      services={TRAVEL_SERVICES}
      img={IMAGES.operator}
      alt="SS Tour & Travels Maruti Ertiga All India Tourist Permit"
      msg="नमस्ते SS Student Travels! मुझे टैक्सी बुकिंग चाहिए। यात्रा: "
      reverse
    />
  </>
);
